import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { firmApi } from "../../utils/api";
import PreapprovalButton from "./PreapprovalButton";
import MainLayout from "../../layouts/MainLayout";

const Preapproval = () => {
  const { firmSlug } = useParams();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFirm = async () => {
      try {
        setLoading(true);
        const data = await firmApi.get(firmSlug);
        if (data.success) {
          setFirm(data.firm);
        } else {
          setError(data.error || "Firm not found");
        }
      } catch (err) {
        console.error("Error fetching firm:", err);
        setError("Unable to load firm details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (firmSlug) {
      fetchFirm();
    }
  }, [firmSlug]);

  if (loading) {
    return (
      <MainLayout>
        <div className="bg-pl-bg min-h-screen flex items-center justify-center">
          <div className="text-pl-text-primary text-18">
            Loading...
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="bg-pl-bg min-h-screen flex flex-col items-center justify-center px-6">
          <div className="bg-pl-bg-elevated rounded-10 shadow-md border border-pl-border p-40 text-center max-w-md">
            <h1 className="text-24 font-semibold text-red-500 mb-10">
              Error
            </h1>
            <p className="text-pl-text-primary mb-20">
              {error}
            </p>
            <Link
              to="/"
              className="text-pl-accent hover:underline"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="bg-pl-bg min-h-screen flex flex-col items-center justify-center px-6 md:px-10 py-50">
        <div className="bg-pl-bg-elevated rounded-10 shadow-md border border-pl-border p-40 max-w-lg w-full">
          <h1 className="text-28 font-display font-medium text-pl-text-primary mb-10 text-center">
            Subscribe to chat.paralegal.lk
          </h1>

          <div className="text-center mb-30">
            <p className="text-14 text-pl-text-primary mb-20">
              Set up a recurring subscription for
            </p>
            <h2 className="text-22 font-display font-semibold text-pl-accent mb-5">
              {firm.name}
            </h2>
            <p className="text-14 text-pl-text-primary">
              chat.paralegal.lk subscription for AI legal research assistant
            </p>
          </div>

          <div className="bg-pl-bg rounded-10 p-20 mb-30">
            <div className="flex justify-between mb-10">
              <span className="text-14 text-pl-text-primary">
                Per Subscription
              </span>
              <span className="text-14 text-pl-text-primary">
                Rs. {firm.subscription_amount?.toLocaleString() || "5,000"}
              </span>
            </div>
            <div className="flex justify-between mb-10">
              <span className="text-14 text-pl-text-primary">
                Number of Subscriptions
              </span>
              <span className="text-14 text-pl-text-primary">
                {firm.num_subscriptions || 1}
              </span>
            </div>
            <div className="flex justify-between mb-10">
              <span className="text-14 text-pl-text-primary">
                Billing Period
              </span>
              <span className="text-14 text-pl-text-primary">
                {firm.subscription_period || "Monthly"}
              </span>
            </div>
            <div className="border-t border-pl-border pt-10 mt-10 flex justify-between">
              <span className="text-14 font-semibold text-pl-text-primary">
                Total Amount
              </span>
              <span className="text-18 font-semibold text-pl-accent">
                Rs. {((firm.subscription_amount || 5000) * (firm.num_subscriptions || 1)).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="mb-30">
            <h3 className="text-14 font-semibold text-pl-text-primary mb-10">
              How it works:
            </h3>
            <ul className="text-14 text-pl-text-primary space-y-2">
              <li className="flex">
                <span className="text-pl-accent font-bold mr-2">1.</span>
                <span>Enter your card details securely via PayHere</span>
              </li>
              <li className="flex">
                <span className="text-pl-accent font-bold mr-2">2.</span>
                <span>You will be charged the subscription amount immediately</span>
              </li>
              <li className="flex">
                <span className="text-pl-accent font-bold mr-2">3.</span>
                <span>Your card will be charged automatically each billing period</span>
              </li>
            </ul>
          </div>

          {firm.preapproval_status === "approved" ? (
            <div className="text-center">
              <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-10 p-15 mb-20">
                <span className="font-semibold">Already Subscribed</span>
                <p className="text-14 mt-5">
                  You already have an active subscription for this account.
                </p>
              </div>
              <a
                href={import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk"}
                className="text-pl-accent hover:underline"
              >
                Go to chat.paralegal.lk →
              </a>
            </div>
          ) : (
            <div className="text-center">
              <PreapprovalButton firm={firm} />
              <p className="text-12 text-pl-text-primary mt-15">
                By authorizing, you agree to our{" "}
                <Link to="/terms" className="text-pl-accent hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-pl-accent hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </div>
          )}
        </div>

        <p className="text-12 text-pl-text-primary mt-20 text-center max-w-md">
          Payments are processed securely by PayHere. Your card details are never stored on our servers.
        </p>
      </div>
    </MainLayout>
  );
};

export default Preapproval;
